import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, useMap, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AirportText } from '@/components/ui/AirportText';
import { fetchPois, type Poi } from '@/services/overpass.service';
import { withCartoKey } from '@/utils/map-tiles';

const ZOOM_START = 5;
const ZOOM_END = 6;
const ZOOM_DURATION_MS = 2800;
const EXTRA_PAUSE_MS = 2000; // 2 s en plus avant de passer au carousel
const TOTAL_DURATION_MS = 5500 + EXTRA_PAUSE_MS; // 7.5 s au total

interface CountryMapWithPoisProps {
    destinationName: string;
    targetCoords: [number, number];
    onAnimationComplete: () => void;
}

function MapController({ onComplete }: { onComplete: () => void }) {
    const map = useMap();

    useEffect(() => {
        map.flyTo(map.getCenter(), ZOOM_END, {
            animate: true,
            duration: ZOOM_DURATION_MS / 1000,
            easeLinearity: 0.25
        });
        const t = setTimeout(onComplete, TOTAL_DURATION_MS);
        return () => clearTimeout(t);
    }, [map, onComplete]);

    return null;
}

function createPoiIcon(name: string): L.DivIcon {
    const escaped = name.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/&/g, '&amp;');
    return L.divIcon({
        className: 'poi-callout-wrapper',
        html: `<div class="poi-callout"><span class="poi-dot"></span><svg class="poi-line" viewBox="0 0 2 20" preserveAspectRatio="none"><line x1="1" y1="0" x2="1" y2="20" stroke="currentColor" stroke-width="1"/></svg><span class="poi-label" title="${escaped}">${escaped}</span></div>`,
        iconSize: [120, 80],
        iconAnchor: [60, 4]
    });
}

export function CountryMapWithPois({
    destinationName,
    targetCoords,
    onAnimationComplete
}: CountryMapWithPoisProps) {
    const [pois, setPois] = useState<Poi[]>([]);

    useEffect(() => {
        fetchPois(targetCoords).then(setPois).catch(() => setPois([]));
    }, [targetCoords]);

    return (
        <div className="absolute inset-0 z-0 bg-slate-200">
            <MapContainer
                center={targetCoords}
                zoom={ZOOM_START}
                zoomControl={false}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                dragging={false}
                attributionControl={false}
                className="w-full h-full leaflet-map-destination leaflet-map-destination--light"
                style={{ background: '#e2e8f0' }}
            >
                <TileLayer
                    url={withCartoKey('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png')}
                    maxZoom={19}
                    maxNativeZoom={19}
                    minZoom={2}
                    keepBuffer={4}
                    subdomains="abcd"
                    attribution="&copy; CARTO"
                />
                {pois.map((poi) => (
                    <Marker
                        key={poi.id}
                        position={[poi.lat, poi.lon]}
                        icon={createPoiIcon(poi.name)}
                    />
                ))}
                <MapController onComplete={onAnimationComplete} />
            </MapContainer>

            <div
                className="absolute inset-0 z-[2] pointer-events-none select-none"
                style={{
                    background:
                        'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, transparent 40%, rgba(0,0,0,0.35) 100%)'
                }}
            />
            <div className="absolute top-1/4 left-0 right-0 flex justify-center z-[3] pointer-events-none select-none">
                <div className="bg-black/40 backdrop-blur-md border-y border-white/20 py-4 w-full text-center">
                    <p className="text-white/80 text-sm mb-1 uppercase tracking-[0.2em]">Destination</p>
                    <h2 className="text-4xl md:text-6xl text-white font-bold drop-shadow-2xl">
                        <AirportText text={`EN ROUTE POUR ${destinationName}`} />
                    </h2>
                </div>
            </div>
        </div>
    );
}
